import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
    Mail, Phone, LogOut, Package, Heart, 
    ChevronRight, ChevronDown, ShieldCheck, RefreshCcw, Camera, ShoppingCart, Briefcase, HelpCircle, FileText
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const policyLinks = [
    { label: 'Refund Policy', path: '/refund-policy', icon: <RefreshCcw className="text-orange-500" /> },
    { label: 'Privacy Policy', path: '/privacy-policy', icon: <ShieldCheck className="text-red-500" /> },
    { label: 'Shipping Policy', path: '/shipping-policy', icon: <Package className="text-blue-500" /> },
    { label: 'Payment Policy', path: '/payment-policy', icon: <ShoppingCart className="text-green-500" /> },
    { label: 'Cancellation Policy', path: '/cancellation-policy', icon: <RefreshCcw className="text-amber-500" /> },
    { label: 'Cookie Policy', path: '/cookie-policy', icon: <FileText className="text-violet-500" /> },
    { label: 'Community Guidelines', path: '/community-guidelines', icon: <ShieldCheck className="text-sky-500" /> },
    { label: 'Grievance Policy', path: '/grievance-policy', icon: <HelpCircle className="text-indigo-500" /> },
    { label: 'Intellectual Property Policy', path: '/intellectual-property-policy', icon: <Briefcase className="text-pink-500" /> },
    { label: 'Terms & Conditions', path: '/terms-and-conditions', icon: <FileText className="text-gray-600" /> },
];

export default function Account() {
    const { user, logout } = useAuth();
    const [loading, setLoading] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const [showPolicies, setShowPolicies] = useState(false);
    const fileInputRef = useRef(null);
    const navigate = useNavigate();

    // Strict user check: Agar user login nahi hai toh 'hasUser' false hoga
    const hasUser = user && (user.email || user._id || user.uid);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => setPreviewImage(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleLogout = async () => {
        if(window.confirm("Are you sure you want to logout?")) {
            setLoading(true);
            try {
                await logout();
                navigate('/account'); 
            } catch (error) {
                console.error("Logout failed", error);
            } finally {
                setLoading(false);
            }
        }
    };

    return (
        <div className="min-h-screen bg-[#F8F9FA] pb-24">
            <div className="max-w-7xl mx-auto md:pt-6">
                
                {/* Header Section */}
                <div className="bg-white px-4 py-4 flex items-center gap-4 border-b border-gray-100 md:rounded-t-2xl">
                    <button onClick={() => navigate(-1)} className="p-1 md:hidden text-gray-800">
                        <ChevronRight className="rotate-180" size={24} />
                    </button>
                    <h1 className="text-base font-bold text-gray-800 uppercase tracking-wide">Account</h1>
                </div>

                {/* Profile / Namaste Card */}
                <div className="bg-white px-5 py-6 mb-2 flex items-center gap-4 shadow-sm border-b">
                    <div className="relative">
                        <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center overflow-hidden border border-orange-200">
                            {hasUser && (previewImage || user.profileImage) ? (
                                <img src={previewImage || user.profileImage} alt="profile" className="w-full h-full object-cover" />
                            ) : (
                                <span className="text-3xl">🙏</span> 
                            )}
                        </div>
                        {hasUser && (
                            <div className="absolute bottom-0 right-0 bg-red-600 p-1.5 rounded-full border-2 border-white text-white cursor-pointer" onClick={() => fileInputRef.current.click()}>
                                <Camera size={12} />
                            </div>
                        )}
                        <input type="file" ref={fileInputRef} onChange={handleImageChange} accept="image/*" className="hidden" />
                    </div>

                    <div className="flex flex-col items-start text-left">
                        {hasUser ? (
                            <>
                                <h2 className="text-lg font-bold text-gray-900 leading-tight">
                                    {user.fullName || user.name || "User"}
                                </h2>
                                <p className="text-xs text-gray-500 mt-1">View and update your profile details</p>
                            </>
                        ) : (
                            <>
                                <Link to="/login" className="px-6 py-1.5 bg-red-400 text-white text-sm font-bold rounded shadow-sm mb-1">
                                    Sign Up
                                </Link>
                                <p className="text-xs text-gray-400">View and update your profile details</p>
                            </>
                        )}
                    </div>
                </div>

                {/* Main Menu Sections */}
                <div className="flex flex-col gap-2">
                    
                    {/* ACTIVITY: Hidden if Logged Out */}
                    {hasUser && (
                        <div className="bg-white border-y border-gray-100">
                            <MenuLink to="/orders" icon={<Package className="text-blue-500" />} label="My Orders" />
                            <MenuLink to="/cart" icon={<ShoppingCart className="text-green-500" />} label="My Cart" />
                            <MenuLink to="/wishlist" icon={<Heart className="text-pink-500" />} label="Wishlist" />
                        </div>
                    )}

                    {/* SUPPORT & LEGAL: Always Visible */}
                    <div className="bg-white border-y border-gray-100">
                        <MenuLink to="/contact-us" icon={<HelpCircle className="text-blue-400" />} label="Help" />
                        
                        <a href="https://vendor.havbit.in/" target="_blank" rel="noopener noreferrer" 
                           className="flex items-center justify-between px-5 py-4 border-b border-gray-50 hover:bg-gray-50 transition-colors">
                            <div className="flex items-center gap-4">
                                <div className="w-8 h-8 flex items-center justify-center">
                                    <Briefcase className="text-orange-500" size={22} />
                                </div>
                                <span className="text-[14px] font-medium text-gray-700">Become a Supplier</span>
                            </div>
                            <ChevronRight className="text-gray-300" size={18} />
                        </a>

                        <div className="border-b border-gray-50">
                            <button
                                type="button"
                                onClick={() => setShowPolicies((prev) => !prev)}
                                className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="w-8 h-8 flex items-center justify-center">
                                        <FileText className="text-orange-400" size={22} />
                                    </div>
                                    <span className="text-[14px] font-medium text-gray-700">Legal and Policies</span>
                                </div>
                                <ChevronDown className={`text-gray-300 transition-transform ${showPolicies ? 'rotate-180' : ''}`} size={18} />
                            </button>

                            {showPolicies && (
                                <div className="bg-gray-50 border-t border-gray-100">
                                    {policyLinks.map((policy) => (
                                        <Link key={policy.path} to={policy.path} className="flex items-center justify-between px-5 py-3 border-b border-gray-100 last:border-b-0 hover:bg-white transition-colors">
                                            <div className="flex items-center gap-3">
                                                <div className="w-7 h-7 flex items-center justify-center">
                                                    {React.cloneElement(policy.icon, { size: 16 })}
                                                </div>
                                                <span className="text-[13px] font-medium text-gray-700">{policy.label}</span>
                                            </div>
                                            <ChevronRight className="text-gray-300" size={16} />
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* CONTACT INFO: Hidden if Logged Out */}
                    {hasUser && (
                        <div className="bg-white px-5 py-5 border-y border-gray-100">
                            <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-4">Contact Information</h3>
                            <div className="space-y-4">
                                <div className="flex items-center gap-4">
                                    <Mail className="text-gray-400" size={18} />
                                    <span className="text-sm text-gray-600 font-medium">{user.email}</span>
                                </div>
                                <div className="flex items-center gap-4">
                                    <Phone className="text-gray-400" size={18} />
                                    <span className="text-sm text-gray-600 font-medium">{user.phone}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* LOGOUT: Hidden if Logged Out */}
                    {hasUser && (
                        <div className="px-5 mt-4">
                            <button onClick={handleLogout} disabled={loading} 
                                className="w-full py-3 bg-white border border-red-100 text-red-600 rounded font-bold text-sm shadow-sm active:bg-red-50 transition-all flex items-center justify-center gap-2">
                                <LogOut size={18} /> {loading ? 'Logging out...' : 'Log Out'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// Reusable Link Component
function MenuLink({ to, icon, label }) {
    return (
        <Link to={to} className="flex items-center justify-between px-5 py-4 border-b border-gray-50 hover:bg-gray-50 transition-colors">
            <div className="flex items-center gap-4">
                <div className="w-8 h-8 flex items-center justify-center">
                    {React.cloneElement(icon, { size: 22 })}
                </div>
                <span className="text-[14px] font-medium text-gray-700">{label}</span>
            </div>
            <ChevronRight className="text-gray-300" size={18} />
        </Link>
    );
}